/**
 * Rules and rates the firm confirms: figures and dates ClientFlow uses that could not be checked
 * on their official page during the October 2026 audit (each is unconfirmed in the audit's rules
 * ledger; the entity and sales tax items are in docs/accuracy/state-compliance-2026-10.json).
 *
 * The value coded here stays in use, labelled "Not confirmed", until an owner or admin confirms or
 * corrects it in Firm Settings › Data and security › Rules and rates. A rule with no coded value
 * shows "Not set: add it in Firm Settings" until someone enters it. Nothing here is filled in from
 * unofficial sources: a figure the audit found conflicting is left unset.
 *
 * Each confirmation or correction is a dated history entry (never changed afterwards). A figure or
 * date is read "as of" a date: the newest entry whose effective date is on or before it, otherwise
 * the coded value. So a correction changes what is due from its effective date on, and anything
 * earlier stays as it was.
 */
import { formatCents } from "./money";

export type RuleSettingKind = "amount" | "rate" | "date-rule" | "holiday-list";
/** amount: whole cents; rate: percent (e.g. 6 for 6%); date-rule: month and day; holiday-list: ISO dates. */
export type RuleSettingValue = number | { month: number; day: number } | string[];

export type RuleSettingDef = {
  key: string;
  /** Plain name shown to staff. */
  name: string;
  kind: RuleSettingKind;
  jurisdiction: "MD" | "VA" | "DC" | "DE";
  /** The value in the app before anyone confirms it; null when the app has none. */
  coded: RuleSettingValue | null;
  /** Where the figure or date is used, in plain words. */
  usedIn: string;
  /** The official page to check it against, from the audit's official-pages list (null when none was recorded). */
  officialUrl: string | null;
  /** What the audit found, for whoever confirms it. */
  note: string;
  /** Entity compliance presets whose fee or due date this is. */
  presets?: string[];
  /** For a fee on an entity filing: how it is labelled on the filing. */
  feeLabel?: string;
  /** Used in a figure clients see (their copy says "estimate" until it is confirmed). */
  clientFacing?: boolean;
};

const SDAT_FORM1 = "https://dat.maryland.gov/SDAT%20Forms/PPR_Forms/2026_Form1.pdf";
const SCC_FEES = "https://www.scc.virginia.gov/businesses/business-faqs/annual-registration-fees/";
const DLCP_FAQ = "https://dlcp.dc.gov/page/corporations-division-business-registration-faqs";
const DLCP_FEES = "https://dlcp.dc.gov/page/dlcp-license-corporate-filing-fees";
const DE_TAXES = "https://corp.delaware.gov/paytaxes/";
const DE_ALT = "https://corp.delaware.gov/alt-entitytaxinstructions/";

export const RULE_SETTINGS: RuleSettingDef[] = [
  {
    key: "md.form1.fee",
    name: "Maryland annual report (SDAT Form 1) filing fee",
    kind: "amount",
    jurisdiction: "MD",
    coded: 30_000,
    usedIn: "Entity compliance: Maryland annual report filings",
    officialUrl: SDAT_FORM1,
    note: "$300 for most entities; nonstock (nonprofit) corporations pay no fee.",
    presets: ["md_annual_report"],
    feeLabel: "Fee",
  },
  {
    key: "md.form1.due",
    name: "Maryland annual report (SDAT Form 1) due date",
    kind: "date-rule",
    jurisdiction: "MD",
    coded: { month: 4, day: 15 },
    usedIn: "Entity compliance: Maryland annual report due dates",
    officialUrl: SDAT_FORM1,
    note: "April 15 each year; a weekend or holiday moves it to the next business day.",
    presets: ["md_annual_report"],
  },
  {
    key: "va.llc_registration.fee",
    name: "Virginia LLC and limited partnership annual registration fee",
    kind: "amount",
    jurisdiction: "VA",
    coded: 5_000,
    usedIn: "Entity compliance: Virginia annual registration fee filings",
    officialUrl: SCC_FEES,
    note: "$50 for LLCs; limited partnerships are believed to pay the same.",
    presets: ["va_llc_registration_fee"],
    feeLabel: "Fee",
  },
  {
    key: "va.corporation_registration.fee",
    name: "Virginia corporation annual registration fee",
    kind: "amount",
    jurisdiction: "VA",
    coded: null,
    usedIn: "Entity compliance: Virginia corporation annual reports",
    officialUrl: SCC_FEES,
    note: "Set by the number of authorized shares; sources differ on the base amount, so the app has no figure.",
    presets: ["va_corporation_annual"],
    feeLabel: "Fee",
  },
  {
    key: "dc.biennial.fee",
    name: "DC biennial report fee",
    kind: "amount",
    jurisdiction: "DC",
    coded: 30_000,
    usedIn: "Entity compliance: DC biennial report filings",
    officialUrl: DLCP_FAQ,
    note: "$300 for for-profit entities.",
    presets: ["dc_biennial_report"],
    feeLabel: "Fee",
  },
  {
    key: "dc.biennial.late_fee",
    name: "DC biennial report late fee",
    kind: "amount",
    jurisdiction: "DC",
    coded: 10_000,
    usedIn: "Entity compliance: DC biennial report filings",
    officialUrl: DLCP_FAQ,
    note: "$100 for for-profit entities.",
    presets: ["dc_biennial_report"],
    feeLabel: "Late fee",
  },
  {
    key: "dc.biennial_nonprofit.fee",
    name: "DC biennial report fee (nonprofit)",
    kind: "amount",
    jurisdiction: "DC",
    coded: 8_000,
    usedIn: "Entity compliance: DC nonprofit biennial report filings",
    officialUrl: DLCP_FEES,
    note: "$80 for nonprofits.",
    presets: ["dc_biennial_report_nonprofit"],
    feeLabel: "Fee",
  },
  {
    key: "dc.biennial_nonprofit.late_fee",
    name: "DC biennial report late fee (nonprofit)",
    kind: "amount",
    jurisdiction: "DC",
    coded: 5_000,
    usedIn: "Entity compliance: DC nonprofit biennial report filings",
    officialUrl: DLCP_FEES,
    note: "$50 for nonprofits.",
    presets: ["dc_biennial_report_nonprofit"],
    feeLabel: "Late fee",
  },
  {
    key: "dc.biennial.due",
    name: "DC biennial report due date",
    kind: "date-rule",
    jurisdiction: "DC",
    coded: { month: 4, day: 1 },
    usedIn: "Entity compliance: DC biennial report due dates",
    officialUrl: DLCP_FAQ,
    note: "April 1, every second year from the year after registration.",
    presets: ["dc_biennial_report", "dc_biennial_report_nonprofit"],
  },
  {
    key: "de.franchise.filing_fee",
    name: "Delaware corporation annual report filing fee",
    kind: "amount",
    jurisdiction: "DE",
    coded: 5_000,
    usedIn: "Entity compliance: Delaware franchise tax reports (the fee is in addition to the franchise tax)",
    officialUrl: DE_TAXES,
    note: "$50 filing fee plus franchise tax.",
    presets: ["de_corporation_franchise"],
    feeLabel: "Filing fee",
  },
  {
    key: "de.franchise.due",
    name: "Delaware corporation franchise tax report due date",
    kind: "date-rule",
    jurisdiction: "DE",
    coded: { month: 3, day: 1 },
    usedIn: "Entity compliance: Delaware franchise tax report due dates",
    officialUrl: DE_TAXES,
    note: "March 1 each year.",
    presets: ["de_corporation_franchise"],
  },
  {
    key: "de.foreign_report.fee",
    name: "Delaware foreign corporation annual report fee",
    kind: "amount",
    jurisdiction: "DE",
    coded: null,
    usedIn: "Entity compliance: Delaware foreign corporation annual reports",
    officialUrl: DE_TAXES,
    note: "Sources conflict ($125 or $250), so the app has no figure.",
    presets: ["de_corporation_foreign_report"],
    feeLabel: "Fee",
  },
  {
    key: "de.foreign_report.due",
    name: "Delaware foreign corporation annual report due date",
    kind: "date-rule",
    jurisdiction: "DE",
    coded: { month: 6, day: 30 },
    usedIn: "Entity compliance: Delaware foreign corporation report due dates",
    officialUrl: DE_TAXES,
    note: "June 30 each year.",
    presets: ["de_corporation_foreign_report"],
  },
  {
    key: "de.alt_entity_tax.amount",
    name: "Delaware LLC, LP and GP annual tax",
    kind: "amount",
    jurisdiction: "DE",
    coded: null,
    usedIn: "Entity compliance: Delaware LLC, LP and GP annual tax",
    officialUrl: DE_ALT,
    note: "Sources conflict ($300 or $400), so the app has no figure.",
    presets: ["de_alternative_entity_tax"],
    feeLabel: "Annual tax",
  },
  {
    key: "dc.sales_tax.rate",
    name: "DC general sales tax rate",
    kind: "rate",
    jurisdiction: "DC",
    coded: null,
    usedIn: "Sales tax: DC registrations and returns",
    officialUrl: "https://otr.cfo.dc.gov/page/sales-and-use-taxes",
    note: "The audit found 6% and 7% (from 1 October 2026) in different places, so the app has no rate.",
  },
  {
    key: "md.holidays",
    name: "Maryland state holidays",
    kind: "holiday-list",
    jurisdiction: "MD",
    coded: null,
    usedIn: "Maryland estimated tax due dates, and Maryland annual report and sales tax due dates (a date on a holiday moves to the next business day)",
    officialUrl: null,
    note: "Federal holidays are already counted. Add the Maryland holidays that are not federal holidays; until then only weekends and federal holidays move Maryland dates.",
    clientFacing: true,
  },
  {
    key: "va.holidays",
    name: "Virginia state holidays",
    kind: "holiday-list",
    jurisdiction: "VA",
    coded: null,
    usedIn: "Virginia estimated tax due dates (Form 760ES: a date on a weekend or legal holiday moves to the next business day) and the Virginia corporation annual report (the business day before)",
    officialUrl: null,
    note: "Federal holidays are already counted. Add the Virginia holidays that are not federal holidays; until then only weekends and federal holidays move Virginia dates.",
    clientFacing: true,
  },
];

export const RULE_SETTING_KEYS = RULE_SETTINGS.map((r) => r.key);
export function ruleSettingDef(key: string): RuleSettingDef | undefined {
  return RULE_SETTINGS.find((r) => r.key === key);
}

/** A confirmation or correction recorded by the firm. */
export type RuleSettingEntry = {
  ruleKey: string;
  value: RuleSettingValue;
  action: "confirmed" | "corrected";
  source: string;
  effectiveFrom: string;
  byName: string | null;
  createdAt: string;
};

export type RuleSettingStatus = "not_set" | "not_confirmed" | "confirmed";
export type ResolvedRuleSetting = { value: RuleSettingValue | null; status: RuleSettingStatus; entry: RuleSettingEntry | null };

const sameValue = (a: RuleSettingValue | null, b: RuleSettingValue | null) => JSON.stringify(a) === JSON.stringify(b);
const newestFirst = (a: RuleSettingEntry, b: RuleSettingEntry) => (a.effectiveFrom === b.effectiveFrom ? (a.createdAt < b.createdAt ? 1 : -1) : a.effectiveFrom < b.effectiveFrom ? 1 : -1);

/**
 * The value in effect on `asOf` and how it is labelled. `coded` overrides the registry's coded
 * value (a firm's own due date from Compliance rules, for example).
 */
export function resolveRuleSetting(def: Pick<RuleSettingDef, "key" | "coded">, entries: readonly RuleSettingEntry[], asOf: string, coded: RuleSettingValue | null = def.coded): ResolvedRuleSetting {
  const mine = entries.filter((e) => e.ruleKey === def.key).sort(newestFirst);
  const inEffect = mine.find((e) => e.effectiveFrom <= asOf);
  if (inEffect) return { value: inEffect.value, status: "confirmed", entry: inEffect };
  if (coded === null) return { value: null, status: "not_set", entry: null };
  // Confirming the coded value later still confirms it for earlier dates: it was right all along.
  const confirmedSame = mine.find((e) => e.action === "confirmed" && sameValue(e.value, coded));
  return confirmedSame ? { value: coded, status: "confirmed", entry: confirmedSame } : { value: coded, status: "not_confirmed", entry: null };
}

export const NOT_SET_LABEL = "Not set: add it in Firm Settings";
export const NOT_CONFIRMED_LABEL = "Not confirmed";

/** "Not set: add it in Firm Settings", "Not confirmed", or "Confirmed by [name] on [date]". */
export function ruleSettingLabel(r: ResolvedRuleSetting, formatDate: (iso: string) => string): string {
  if (r.status === "not_set") return NOT_SET_LABEL;
  if (r.status === "not_confirmed" || !r.entry) return NOT_CONFIRMED_LABEL;
  return `Confirmed by ${r.entry.byName ?? "a former team member"} on ${formatDate(r.entry.createdAt.slice(0, 10))}`;
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** The value in words: "$300", "6%", "April 15", or a count of dates. */
export function ruleSettingValueText(kind: RuleSettingKind, v: RuleSettingValue | null): string {
  if (v === null) return NOT_SET_LABEL;
  // Fees are whole-dollar settings: cents only when there are some.
  if (kind === "amount" && typeof v === "number") return formatCents(v, { showCents: v % 100 !== 0 });
  if (kind === "rate" && typeof v === "number") return `${v}%`;
  if (kind === "date-rule" && typeof v === "object" && !Array.isArray(v)) return `${MONTH_NAMES[v.month - 1]} ${v.day}`;
  if (kind === "holiday-list" && Array.isArray(v)) return v.length === 0 ? "No state holidays beyond the federal ones" : `${v.length} date${v.length === 1 ? "" : "s"}`;
  return "";
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const validIso = (s: string) => ISO.test(s) && new Date(`${s}T00:00:00Z`).toISOString().slice(0, 10) === s;

/** Checks a value entered for a rule; returns an error in plain words, or null. */
export function ruleSettingValueError(kind: RuleSettingKind, v: unknown): string | null {
  switch (kind) {
    case "amount":
      return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 100_000_000 ? null : "Enter an amount from $0 to $1,000,000.";
    case "rate":
      return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100 && Math.round(v * 1000) === v * 1000 ? null : "Enter a rate from 0 to 100 percent, with at most three decimal places.";
    case "date-rule": {
      const x = v as { month?: unknown; day?: unknown } | null;
      if (!x || typeof x !== "object" || Array.isArray(x) || Object.keys(x).some((k) => k !== "month" && k !== "day")) return "Enter a month and day.";
      const { month, day } = x as { month: number; day: number };
      if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(day) || day < 1) return "Enter a month and day.";
      return day <= new Date(Date.UTC(2024, month, 0)).getUTCDate() ? null : "That day does not exist in that month.";
    }
    case "holiday-list":
      return Array.isArray(v) && v.length <= 60 && v.every((d) => typeof d === "string" && validIso(d)) ? null : "Enter each holiday as a date (YYYY-MM-DD), up to 60 dates.";
  }
}

/** Holiday-list values are kept sorted and without repeats. */
export function normalizeRuleSettingValue(kind: RuleSettingKind, v: RuleSettingValue): RuleSettingValue {
  return kind === "holiday-list" && Array.isArray(v) ? [...new Set(v)].sort() : v;
}

/** A state's own holidays in effect for a date (null when the firm hasn't added any). */
export function stateHolidaysAsOf(entries: readonly RuleSettingEntry[], state: string, date: string): string[] | null {
  const def = ruleSettingDef(state === "MD" ? "md.holidays" : state === "VA" ? "va.holidays" : "");
  if (!def) return null;
  const r = resolveRuleSetting(def, entries, date);
  return Array.isArray(r.value) ? r.value : null;
}
